import React, { useState } from 'react';
import {
  Calendar,
  Plus,
  Trash2,
  Clock,
  Users,
  MapPin,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/Label';
import { Toast } from './ui/Toast';
import ConfirmDialog from './ConfirmDialog';
import useEventsParams from '../hooks/useEventsParams';
import EnTeteParametrage from './parametrage/EnTeteParametrage';
import EnTeteSection from './parametrage/EnTeteSection';
import CarteReglages from './parametrage/CarteReglages';
import ChargementSection from './parametrage/ChargementSection';
import ChampSelect from './ui/ChampSelect';

/**
 * Paramétrage des évènements de la page d'accueil.
 *
 * ----------------------------------------------------------------------------
 * Refondue le 11 septembre 2026 sur la norme
 * ----------------------------------------------------------------------------
 * La page portait son propre encadré, ses propres titres de section et son
 * propre système de messages. Elle suit maintenant PARAMETRAGE-MODULES.md :
 * conteneur unique, cartes à barre d'accent, un seul chemin de retour et un
 * seul chemin de message.
 *
 * Deux défauts corrigés au passage :
 *
 * 1. **Deux systèmes de retour pour un même évènement.** Chaque message partait
 *    dans un bandeau vert ou rouge ET dans un toast : on lisait la même phrase
 *    deux fois, à deux endroits, avec deux durées de vie. Seul le toast reste.
 *
 * 2. **Le bouton portait le message d'erreur.** Quand une catégorie était
 *    pleine, son libellé devenait « Maximum 3 évènements atteint pour cette
 *    catégorie » — un bouton large comme une phrase, qui ne disait plus ce
 *    qu'il faisait. Le bouton garde son libellé ; la raison du blocage
 *    s'affiche à côté.
 */

const MAX_EVENTS_PER_CATEGORY = 3;
const MAX_TITLE_LENGTH = 200;

const ACCENT = '#246990';
const ACCENT_SOMBRE = '#1a4f6e';

const CATEGORIES = [
  {
    id: 0,
    name: 'Réunions',
    type: 'meeting',
    color: 'from-[#246990] to-[#3084b5]',
    accent: '#246990',
    icon: Users,
    description: 'Conseils de classe, réunions parents-profs…',
  },
  {
    id: 1,
    name: 'Évènements',
    type: 'event',
    color: 'from-[#319190] to-[#63b5b4]',
    accent: '#319190',
    icon: Calendar,
    description: 'Photos de classe, spectacles, journées spéciales…',
  },
  {
    id: 2,
    name: 'Activités',
    type: 'activity',
    color: 'from-[#c66d59] to-[#ae7467]',
    accent: '#c66d59',
    icon: MapPin,
    description: 'Sorties pédagogiques, visites, ateliers…',
  },
];

const categorieDe = (id) => CATEGORIES.find((c) => c.id === id) || CATEGORIES[0];

const EventsSettingsSection = ({ onBackToDashboard }) => {
  const {
    events,
    eventsByCategory,
    addEvent,
    deleteEvent,
    loading: eventsLoading,
  } = useEventsParams();

  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Un seul chemin de message. Il y en avait deux — bandeau et toast — qui
  // disaient la même phrase avec deux durées de vie différentes.
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const avertir = (message, type = 'error') => setToast({ show: true, message, type });
  const confirmer = (message) => setToast({ show: true, message, type: 'success' });

  const [newEvent, setNewEvent] = useState({
    date: '',
    title: '',
    type: 'meeting',
    category: 0,
    color_gradient: CATEGORIES[0].color,
  });

  const categorieCourante = categorieDe(newEvent.category);
  const evenementsDeLaCategorie = eventsByCategory[newEvent.category] || [];
  const categoriePleine = evenementsDeLaCategorie.length >= MAX_EVENTS_PER_CATEGORY;
  const champsManquants = !newEvent.date.trim() || !newEvent.title.trim();

  const handleCategoryChange = (categoryId) => {
    const categorie = categorieDe(parseInt(categoryId, 10));
    setNewEvent((prev) => ({
      ...prev,
      category: categorie.id,
      type: categorie.type,
      color_gradient: categorie.color,
    }));
  };

  const handleAddEvent = async () => {
    if (categoriePleine) {
      avertir(`Maximum ${MAX_EVENTS_PER_CATEGORY} évènements par catégorie (${categorieCourante.name})`);
      return;
    }
    if (champsManquants) {
      avertir('Veuillez remplir tous les champs obligatoires');
      return;
    }
    if (newEvent.title.length > MAX_TITLE_LENGTH) {
      avertir(`Le titre ne peut pas dépasser ${MAX_TITLE_LENGTH} caractères`);
      return;
    }

    setSaving(true);
    try {
      const result = await addEvent(newEvent);
      if (result.success) {
        confirmer('Évènement ajouté');
        setNewEvent({
          date: '',
          title: '',
          type: 'meeting',
          category: 0,
          color_gradient: CATEGORIES[0].color,
        });
      } else {
        avertir(result.error || "Erreur lors de l'ajout");
      }
    } catch (error) {
      avertir('Erreur de connexion au serveur');
    } finally {
      setSaving(false);
    }
  };

  // Le dialogue ne se ferme pas tout seul : `ConfirmDialog` laisse la main au
  // parent, justement pour qu'il puisse montrer l'attente puis fermer quand
  // c'est fait. Sans ces deux lignes, il restait ouvert apres la suppression et
  // on croyait que rien ne s'etait passe.
  const handleDeleteEvent = async () => {
    if (!itemToDelete || deleting) return;
    setDeleting(true);
    try {
      const result = await deleteEvent(itemToDelete.id);
      if (result.success) {
        confirmer('Évènement supprimé');
        setShowDeleteConfirm(false);
        setItemToDelete(null);
      } else {
        // En cas d'echec, le dialogue reste ouvert : le message passe dans le
        // toast et l'utilisateur peut reessayer sans tout recommencer.
        avertir(result.error || 'Erreur lors de la suppression');
      }
    } catch (error) {
      avertir('Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  const enTete = (
    <EnTeteParametrage
      onRetour={onBackToDashboard}
      icone={Calendar}
      titre="Paramétrage des évènements"
      sousTitre="Réunions, évènements et activités affichés sur la page d'accueil"
    />
  );

  if (eventsLoading) {
    return (
      <div className="w-full">
        {enTete}
        <div className="bg-white rounded-xl shadow-[0_1px_4px_rgba(0,0,0,0.06)] border border-gray-200/80 p-6">
          <ChargementSection accent={ACCENT} libelle="Chargement des évènements…" />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}

      {enTete}

      <div className="bg-white rounded-xl shadow-[0_1px_4px_rgba(0,0,0,0.06)] border border-gray-200/80 p-6">
        <div className="space-y-5">
          <EnTeteSection
            icone={Calendar}
            titre="Évènements de la page d'accueil"
            sousTitre={`Trois catégories, ${MAX_EVENTS_PER_CATEGORY} évènements au maximum dans chacune`}
            accent={ACCENT}
            accentSombre={ACCENT_SOMBRE}
          />

          {/* ------------------------------------------------------------ */}
          {/* Ajouter                                                       */}
          {/* ------------------------------------------------------------ */}
          <CarteReglages
            titre="Ajouter un évènement"
            accent={categorieCourante.accent}
            compteur={`${evenementsDeLaCategorie.length}/${MAX_EVENTS_PER_CATEGORY} dans ${categorieCourante.name}`}
          >
            <div className="grid gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <Label className="text-sm text-gray-700">Catégorie *</Label>
                {/* Liste deroulante de la charte : un `<select>` natif ne prend
                    ni la hauteur, ni le rayon, ni la couleur de focus du reste
                    de l'application, et son menu est rendu par le systeme —
                    donc different d'un poste a l'autre. */}
                <div className="mt-1">
                  <ChampSelect
                    valeur={newEvent.category}
                    onChange={handleCategoryChange}
                    options={CATEGORIES.map((categorie) => {
                      const occupes = (eventsByCategory[categorie.id] || []).length;
                      const pleine = occupes >= MAX_EVENTS_PER_CATEGORY;
                      return {
                        valeur: categorie.id,
                        libelle: `${categorie.name} — ${occupes}/${MAX_EVENTS_PER_CATEGORY}${pleine ? ' — complète' : ''}`
                      };
                    })}
                  />
                </div>
                <p className="mt-1.5 text-[11px] text-gray-400">
                  {categorieCourante.description}
                </p>
              </div>

              <div>
                <Label className="text-sm text-gray-700">Titre *</Label>
                <Input
                  value={newEvent.title}
                  onChange={(e) => setNewEvent((prev) => ({ ...prev, title: e.target.value }))}
                  placeholder="Réunion parents-professeurs"
                  maxLength={MAX_TITLE_LENGTH}
                  className="mt-1 h-9 rounded-lg border-gray-200 focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
                />
                <p className="mt-1.5 text-[11px] text-gray-400">
                  {MAX_TITLE_LENGTH - newEvent.title.length} caractères restants
                </p>
              </div>

              <div>
                <Label className="text-sm text-gray-700">Date *</Label>
                <Input
                  value={newEvent.date}
                  onChange={(e) => setNewEvent((prev) => ({ ...prev, date: e.target.value }))}
                  placeholder="15 mars"
                  maxLength={50}
                  className="mt-1 h-9 rounded-lg border-gray-200 focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
                />
                <p className="mt-1.5 text-[11px] text-gray-400">
                  Format libre : « 15 mars », « 20-25 avril », « semaine du 10 mai »
                </p>
              </div>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-gray-100 pt-4">
              {/* Le bouton garde son libellé. Il portait le message d'erreur :
                  « Maximum 3 évènements atteint pour cette catégorie » tenait
                  lieu de libellé, et le bouton ne disait plus ce qu'il faisait. */}
              <Button
                onClick={handleAddEvent}
                disabled={saving || categoriePleine || champsManquants}
                className="h-9 min-w-[168px] justify-center gap-2 bg-[#246990] hover:bg-[#1f5a7a] text-white disabled:bg-gray-300"
              >
                <Plus className="w-4 h-4" />
                {saving ? 'Ajout…' : "Ajouter l'évènement"}
              </Button>

              {categoriePleine && (
                <p className="text-xs text-amber-700">
                  La catégorie « {categorieCourante.name} » est complète. Supprimez-en un
                  pour en ajouter un autre.
                </p>
              )}
              {!categoriePleine && champsManquants && (
                <p className="text-xs text-gray-400">
                  Un titre et une date sont requis.
                </p>
              )}
            </div>
          </CarteReglages>

          {/* ------------------------------------------------------------ */}
          {/* Ce qui est publié                                             */}
          {/* ------------------------------------------------------------ */}
          <CarteReglages
            titre="Publiés sur la page d'accueil"
            accent={ACCENT}
            compteur={`${events.length} évènement${events.length > 1 ? 's' : ''}`}
          >
            <div className="grid gap-5 md:grid-cols-3">
              {CATEGORIES.map((categorie) => {
                const liste = eventsByCategory[categorie.id] || [];
                const pleine = liste.length >= MAX_EVENTS_PER_CATEGORY;
                const Icone = categorie.icon;

                return (
                  <div key={categorie.id} className="space-y-3">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-1.5 h-4 rounded-full flex-shrink-0"
                        style={{ backgroundColor: categorie.accent }}
                      />
                      <Icone size={14} className="text-gray-400" />
                      <span className="text-sm font-semibold text-gray-800">{categorie.name}</span>
                      <span
                        className={`ml-auto text-[11px] font-semibold ${
                          pleine ? 'text-amber-700' : 'text-gray-400'
                        }`}
                      >
                        {liste.length}/{MAX_EVENTS_PER_CATEGORY}
                      </span>
                    </div>

                    <div className="space-y-2">
                      {liste.length === 0 ? (
                        <div className="h-24 rounded-lg border-2 border-dashed border-gray-200 flex flex-col items-center justify-center gap-1">
                          <Calendar size={20} className="text-gray-300" />
                          <span className="text-[11px] text-gray-400">Aucun évènement</span>
                        </div>
                      ) : (
                        liste.map((event) => (
                          <div
                            key={event.id}
                            className={`group relative rounded-lg bg-gradient-to-r ${event.color_gradient} p-3 text-white`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-semibold leading-tight">{event.title}</p>
                                <p className="mt-1 flex items-center text-[11px] text-white/80">
                                  <Clock size={10} className="mr-1 flex-shrink-0" />
                                  {event.date}
                                </p>
                                <p className="mt-2 text-[11px] text-white/70">
                                  #{event.ordre}
                                  {event.updated_at && (
                                    <> · {new Date(event.updated_at).toLocaleDateString('fr-FR')}</>
                                  )}
                                </p>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  setItemToDelete(event);
                                  setShowDeleteConfirm(true);
                                }}
                                aria-label={`Supprimer ${event.title}`}
                                className="flex-shrink-0 rounded-md p-1 text-white/70 opacity-0 transition-opacity hover:bg-white/20 hover:text-white group-hover:opacity-100 focus:opacity-100"
                              >
                                <Trash2 size={13} />
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CarteReglages>
        </div>
      </div>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => { if (!deleting) { setShowDeleteConfirm(false); setItemToDelete(null); } }}
        onConfirm={handleDeleteEvent}
        isLoading={deleting}
        title="Supprimer l'évènement"
        description={`Êtes-vous sûr de vouloir supprimer « ${itemToDelete?.title} » ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="warning"
      />
    </div>
  );
};

export default EventsSettingsSection;